import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/data/content";

export const Logo = ({ compact = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
    <svg width="38" height="38" viewBox="0 0 64 64" aria-hidden="true" className="shrink-0">
      <path d="M12 56V30C12 17 20 8 32 8s20 9 20 22v26" fill="none" stroke="#D4AF37" strokeWidth="4" strokeLinecap="square" />
      <path d="M20 56V32c0-8 5-14 12-14s12 6 12 14v24" fill="none" stroke="#F2EFE6" strokeWidth="2.5" />
      <line x1="6" y1="56" x2="58" y2="56" stroke="#D4AF37" strokeWidth="4" />
    </svg>
    {!compact && (
      <span className="leading-none">
        <span className="block font-display text-base sm:text-lg font-extrabold tracking-[0.08em] text-ivory group-hover:text-gold transition-colors duration-300">
          SMART NAGAR NIGAM
        </span>
        <span className="mt-1 hidden sm:block text-[10px] uppercase tracking-[0.3em] text-white/50">
          Governance That Reaches Every Ward
        </span>
      </span>
    )}
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-2xl backdrop-saturate-150">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-3 md:px-10 md:py-4">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-white/70 hover:text-ivory"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/register"
            data-testid="nav-register-cta"
            className="ml-2 inline-flex items-center gap-2 bg-gold px-6 py-3 font-display text-[13px] font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light"
          >
            Register <ArrowUpRight size={15} />
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="p-2 -mr-2 text-ivory lg:hidden"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <nav
          className="max-h-[calc(100svh-60px)] overflow-y-auto border-t border-white/10 bg-obsidian px-5 py-6 lg:hidden"
          aria-label="Mobile"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1">
            {[...NAV_LINKS, { to: "/register", label: "Register" }].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`border-b border-white/5 py-4 font-display text-2xl font-bold tracking-tight transition-colors duration-300 ${
                  location.pathname === l.to ? "text-gold" : "text-ivory hover:text-gold"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-white/10 bg-obsidian" data-testid="site-footer">
    <div className="mx-auto max-w-[1600px] px-5 py-16 md:px-10 md:py-24">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Logo />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/55">
            A permanent, non-partisan civic intelligence institution transforming
            ward-level governance across India through data-driven accountability,
            citizen participation, public leadership, and technology-enabled urban
            transformation.
          </p>
          <p className="mt-6 font-display text-xs uppercase tracking-[0.3em] text-gold">
            Civic Intelligence · Better Cities · Empowered Citizens
          </p>
        </div>
        <div className="md:col-span-3">
          <h3 className="mb-5 text-xs uppercase tracking-[0.25em] text-white/40">Explore</h3>
          <ul className="space-y-3">
            {[...NAV_LINKS, { to: "/register", label: "Register" }].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-sm text-white/65 transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <h3 className="mb-5 text-xs uppercase tracking-[0.25em] text-white/40">Contact</h3>
          <ul className="space-y-4 text-sm text-white/65">
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 shrink-0 text-gold" />
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="transition-colors duration-300 hover:text-gold">
                {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 shrink-0 text-gold" />
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="transition-colors duration-300 hover:text-gold">
                {CONTACT.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-gold" />
              <span data-testid="footer-address">{CONTACT.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
        <p className="text-xs text-white/40">© 2026 Smart Nagar Nigam. All Rights Reserved.</p>
        <p className="text-xs uppercase tracking-[0.2em] text-white/40">
          From Wards 2 the World — smartnagarnigam.com
        </p>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-obsidian">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);



export default Layout;
